import { memo, useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { CircularProgress, Typography } from '@mui/material';
import { ContentDiv, LoaderBox, Wrapper } from './WordPreview.styles';
import { useTranslation } from 'react-i18next';
interface Props {
  fileBlob: Blob | null;
}

const WordPreviewBase = ({ fileBlob }: Props) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fileBlob) return;

    const renderDoc = async () => {
      if (!containerRef.current) return;

      setLoading(true);
      setError(null);

      containerRef.current.innerHTML = '';

      try {
        const arrayBuffer = await fileBlob.arrayBuffer();

        if (!containerRef.current) return;

        await renderAsync(arrayBuffer, containerRef.current, undefined, {
          className: 'docx-content',
          inWrapper: false,
          ignoreWidth: false,
          experimental: true,
          useBase64URL: true,
        });
      } catch (err) {
        setError(t('user.detail.file.preview.failedToDisplay'));
      } finally {
        setLoading(false);
      }
    };

    renderDoc();
  }, [fileBlob, t]);

  if (!fileBlob) return null;

  const isContentVisible = !loading && !error;

  return (
    <Wrapper>
      {loading && (
        <LoaderBox>
          <CircularProgress />
        </LoaderBox>
      )}

      {error && !loading && (
        <LoaderBox>
          <Typography color="error">{error}</Typography>
        </LoaderBox>
      )}

      <ContentDiv ref={containerRef} isVisible={isContentVisible} />
    </Wrapper>
  );
};

export const WordPreview = memo(WordPreviewBase) as typeof WordPreviewBase;
